import 'src/styles/ui/resource-title-card.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';

import MaintenanceHealthBadge from '../maintenance/MaintenanceHealthBadge';

/**
 * ResourceTitleCard Component - Displays a prominent title card with icon
 * Used for showing main resource information like container IDs, entities, etc.
 *
 * @param {Object} props
 * @param {Object} props.icon - FontAwesome icon to display
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle text (e.g., UUID, identifier)
 * @param {boolean} props.monoSubtitle - Monospace subtitle (default; set false for plain text)
 * @param {string} props.iconColor - Background color for icon wrapper (defaults to blue gradient)
 * @param {string} props.className - Additional CSS classes
 */
const ResourceTitleCard = ({
  icon,
  title,
  titleHref,
  titleIcon,
  subtitle,
  monoSubtitle,
  iconColor,
  className,
  instanceTargetUri,
  ...props
}) => {
  const cardClasses = classNames('resource-title-card', className);

  return (
    <div className={cardClasses} {...props}>
      <div
        className='resource-title-card__icon-wrapper'
        style={iconColor ? { background: iconColor } : undefined}
      >
        <FontAwesomeIcon icon={icon} className='resource-title-card__icon' />
      </div>
      <div className='resource-title-card__content'>
        <h2 className='resource-title-card__title'>
          {titleHref ? (
            <Link to={titleHref} className='resource-title-card__title-link'>
              {titleIcon && (
                <FontAwesomeIcon icon={titleIcon} className='resource-title-card__title-icon' />
              )}
              {title}
            </Link>
          ) : (
            title
          )}
        </h2>
        {subtitle && (
          <span
            className={classNames('resource-title-card__subtitle', {
              'resource-title-card__subtitle--text': !monoSubtitle,
            })}
          >
            {subtitle}
          </span>
        )}
        <MaintenanceHealthBadge equipmentUri={instanceTargetUri} className='mt-2' />
      </div>
    </div>
  );
};

ResourceTitleCard.propTypes = {
  icon: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  titleHref: PropTypes.string,
  titleIcon: PropTypes.object,
  subtitle: PropTypes.string,
  monoSubtitle: PropTypes.bool,
  iconColor: PropTypes.string,
  className: PropTypes.string,
  instanceTargetUri: PropTypes.string.isRequired,
};

ResourceTitleCard.defaultProps = {
  titleHref: null,
  titleIcon: null,
  subtitle: null,
  monoSubtitle: true,
  iconColor: null,
  className: '',
};

export default ResourceTitleCard;
